import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ArrowLeft, TrendingUp } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { AnalyzeRecordsButton } from "@/components/patient/AnalyzeRecordsButton";
import { LabResultsTable } from "@/components/patient/LabResultsTable";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState, useEffect } from "react";

export default function PacienteResultados() {
  const { id = "" } = useParams();
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const queryClient = useQueryClient();

  // Invalida o cache ao montar o componente
  useEffect(() => {
    if (id) {
      console.log("🔄 Invalidando cache para patient_id:", id);
      queryClient.invalidateQueries({ queryKey: ["lab-results", id] });
    }
  }, [id, queryClient]);

  const { data: patient } = useQuery({
    queryKey: ["patient", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("patients_onboarding")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!id && id !== undefined && id !== '',
  });

  const { data: labResults, isLoading, error, refetch } = useQuery<any[]>({
    queryKey: ["lab-results", id],
    queryFn: async () => {
      console.log("🔍 Iniciando busca de resultados para patient_id:", id);

      // Query com JOIN para buscar nome do arquivo diretamente
      const { data, error } = await supabase
        .from("lab_results_onboarding")
        .select(`
          *,
          smartfile_sessions:source_session_id (
            file_name,
            content_type
          ),
          lab_subcategories:subcategory_id (
            name,
            category_id
          )
        `)
        .eq("patient_id", id)
        .order("collected_at", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false });

      if (error) {
        console.error("❌ Error loading lab results:", error);
        throw error;
      }

      console.log("📊 Raw data recebida:", data?.length || 0, "registros");

      if (!data || data.length === 0) {
        console.warn("⚠️ Nenhum dado retornado da query");
        return [];
      }

      // Mapear os dados com as informações do JOIN
      const enrichedData = data.map((result: any) => ({
        ...result,
        subcategory_name: result.lab_subcategories?.name,
        source_file_name: result.smartfile_sessions?.file_name,
        source_content_type: result.smartfile_sessions?.content_type,
      }));

      console.log("✅ Dados enriquecidos:", enrichedData.length, "registros");
      return enrichedData;
    },
    enabled: !!id && id !== undefined && id !== '',
    staleTime: 0,
    gcTime: 0,
  });

  // Log de sucesso/erro via useEffect
  useEffect(() => {
    if (error) {
      console.error("❌ Erro ao carregar resultados:", error);
    }
    if (labResults) {
      console.log("✅ Query bem-sucedida:", labResults.length, "resultados carregados");
    }
  }, [error, labResults]);

  // Debug detalhado
  console.log("🔍 Debug PacienteResultados:", {
    id,
    hasId: !!id,
    isLoading,
    hasError: !!error,
    dataLength: labResults?.length,
    rawDataSample: labResults?.[0],
  });

  const { data: subcategories } = useQuery({
    queryKey: ["subcategories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lab_subcategories")
        .select("id, name, category_id")
        .order("name");

      if (error) throw error;
      return data;
    },
  });

  const filteredResults = labResults?.filter((result) => {
    if (selectedCategory === "all") return true;
    if (selectedCategory === "uncategorized") return !result.subcategory_id;
    return result.subcategory_id === selectedCategory;
  }) || [];

  return (
    <div className="space-y-6">
      {/* Header com info do paciente */}
      <header className="bg-card border rounded-xl p-6">
        <Link to={`/app/pacientes/${id}`}>
          <Button variant="ghost" size="sm" className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar ao perfil
          </Button>
        </Link>

        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-gradient-primary grid place-items-center font-bold text-lg text-primary-foreground">
            {patient?.full_name?.slice(0, 2).toUpperCase() || "??"}
          </div>
          <div>
            <h1 className="text-2xl font-bold">{patient?.full_name || "Carregando..."}</h1>
            <p className="text-sm text-muted-foreground">
              CPF: {patient?.cpf || "Não informado"}
            </p>
          </div>
        </div>
      </header>

      {/* Filtros e Ações */}
      <section className="bg-card border rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold">Resultados Laboratoriais</h2>
            </div>
            <span className="text-sm text-muted-foreground">
              {filteredResults.length} {filteredResults.length === 1 ? "resultado" : "resultados"}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Todas as categorias" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as categorias</SelectItem>
                <SelectItem value="uncategorized">Sem categoria</SelectItem>
                {subcategories?.map((sub) => (
                  <SelectItem key={sub.id} value={sub.id}>
                    {sub.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {id && <AnalyzeRecordsButton patientId={id} />}
          </div>
        </div>

        {isLoading && (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        )}

        {!isLoading && (
          <LabResultsTable
            results={filteredResults}
            subcategories={subcategories || []}
            onRefetch={refetch}
          />
        )}
      </section>
    </div>
  );
}
